// ** React Imports
import { useState, useEffect} from "react";
import {useParams, useNavigate, useLocation } from "react-router-dom";

// ** MUI Imports
import {
  Avatar,
  AvatarGroup,
  IconButton,
  Button,
  Box,
  Typography,
  Card,
  CardContent,
  CardHeader,
  Menu,
  MenuItem,
  FormLabel,
  FormControl,
  FormControlLabel,
  Grid,
  Divider,
  Checkbox,
  ToggleButton,
  ToggleButtonGroup,
  CircularProgress,
  Backdrop,
  InputAdornment
} from "@mui/material";
import { styled, useTheme } from "@mui/material/styles";
import Tooltip, { tooltipClasses } from "@mui/material/Tooltip";
import Tab from "@mui/material/Tab";
import TabList from "@mui/lab/TabList";
import TabPanel from "@mui/lab/TabPanel";
import TabContext from "@mui/lab/TabContext";
import TimelineDot from "@mui/lab/TimelineDot";
import TimelineContent from "@mui/lab/TimelineContent";
import TimelineSeparator from "@mui/lab/TimelineSeparator";
import TimelineConnector from "@mui/lab/TimelineConnector";
import MuiTimeline from "@mui/lab/Timeline";
import TimelineItem from "@mui/lab/TimelineItem";
import { LocalizationProvider, DatePicker, TimePicker } from "@mui/x-date-pickers-pro";
import { AdapterDayjs } from "@mui/x-date-pickers-pro/AdapterDayjs";
import { DataGrid } from "@mui/x-data-grid";
import { DataGridPro } from '@mui/x-data-grid-pro';

import dayjs from "dayjs";
import _ from 'lodash';
// ** Util Imports
import { hexToRGBA } from "../../components/utils/hex-to-rgba";

// ** Custom Component Imports
import CustomTextField from "../../components/mui/text-field";
import CustomChip from "../../components/mui/chip";

// ** Component Imports
import DateRangeCalendar from "../Customers/component/DateRangeCalendar";
import WorkRequirements from "../Assessments/component/WorkRequirements";
import AddServiceDialog from "../Assessments/component/AddService";
import AddBundleDialog from "screens/Assessments/component/AddBundle";
import SpecialInstructionsDialog from "components/SpecialInstructions";
import UnsavedChangesDialog from "components/UnsavedChangesDialog";
import AddJobsiteDialog from "screens/Customers/component/AddJobsite";

// ** Icon Imports
import Icon from "../../components/icon";
import FiberManualRecordIcon from "@mui/icons-material/FiberManualRecord";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
// ** Custom Components Imports
import CustomAutocomplete from "components/mui/autocomplete";
import CustomAvatar from "components/mui/avatar";
import OptionsMenu from "../../components/option-menu";

// ** Third Party Components
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import withReactContent from "sweetalert2-react-content";
import Swal from "sweetalert2";

// ** Layout Import
import UserLayout from "../../components/layouts/UserLayout";
import { useIndexLogic } from "screens/Customers/logic/indexLogic";
import { useCustomerLogic } from "screens/Customers/logic/CustomerLogic";
import { useUnsavedChanges } from "components/UnsavedChangesContext";
import { useCreateWorkOrderLogic } from "./logic/create-work-order-logic";
import { GetJobsite } from "screens/Customers/logic/GetJobsites";
import { GetServiceRequestRaw } from "screens/Requests/logic/GetServiceRequest";
import AddContactDialog from "screens/Customers/component/AddContact";
import TagManagementDialog from "screens/Customers/component/TagManagement";
import LinkedRecordsComponent from "screens/Requests/component/LinkedRecordsComponent";
import NotesComponent from "screens/Requests/component/NotesComponent";
import AssignToComponent from "screens/Assessments/component/AssignToComponent";
import JobScheduleDialog from "./component/JobSchedule";
import { getStatusRules, canTransition} from "./logic/StatusManager";
import { useServiceLogic } from "screens/Requests/logic/ServiceLogic";
import PreAttachment from "screens/Requests/component/PreAttachment";
import { useWorkRequirementLogic } from "./logic/work-requirement-logic";
import { useAssignToLogic } from "screens/Assessments/logic/AssignToLogic";
import BulkScheduleDialog from "./component/BulkScheduleDialog";
import { useJobListLogic } from "./logic/job-list-logic";
import JobDetailDialog from "./component/JobDetail";
import AttachmentsComponent from "screens/Requests/component/AttachmentsComponent";
import { SendNotification } from "components/SendNotification";
import { SendWODepositInvoice } from "./logic/SendWODepositInvoice";
import { useJobsiteVisitLogic } from "./logic/jobsite-visit-logic";

// Styled Timeline component
const Timeline = styled(MuiTimeline)({
  "& .MuiTimelineItem-root": {
    width: "100%",
    "&:before": {
      display: "none",
    },
  },
});

// ** Styled Components
const HtmlTooltip = styled(({ className, ...props }) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
  [`& .${tooltipClasses.tooltip}`]: {
    backgroundColor: theme.palette.mode === 'dark' ? '#1B2431' : theme.palette.common.white,
    minWidth: 220,
    maxWidth: 440,
    border: "1px solid #E1E4EA",
  },
}));


const dropdownItemsStyle = {
  px: 6,
  py: 2,
  "&:hover": {
    backgroundColor: "action.hover",
    cursor: "pointer",
  },
};


const Customersteps = [
  {
    icon: "tabler:user",
    title: "Profile Information",
  },
  {
    icon: "tabler:address-book",
    title: "Add Contact",
  },
  {
    icon: "tabler:align-box-bottom-center",
    title: "Add Jobsite",
  },
];

const stepsRequestDetails = [
  {
    serial: "1",
    title: "Basic Information",
    icon: "tabler:list-details",
  },
  {
    serial: "2",
    title: "Work Requirements",
    icon: "tabler:align-box-left-stretch",
  },
  {
    serial: "3",
    title: "Billing Information",
    icon: "tabler:align-box-left-stretch",
  },
  {
    serial: "4",
    title: "Link Records",
    icon: "tabler:link",
  },
  {
    serial: "5",
    title: "Notes",
    icon: "tabler:ballpen",
  },
  {
    serial: "6",
    title: "Assign To",
    icon: "tabler:user-share",
  },
];

const parseAvailabilityDays = (daysString) => {
  try {
    const daysArray = JSON.parse(daysString);
    // Ensure that only numbers are kept in the array
    return daysArray.filter((item) => typeof item === 'number');
  } catch (error) {
    //console.error('Error parsing availabilityDays:', error);
    return [];
  }
};
const CustomChipWithDropdown = ({ tag, onSelect }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const theme = useTheme();

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleSelect = (selectedTag) => {
    onSelect(selectedTag);
    handleClose();
  };

  const statusColors = {
    "Not Viewed": "info",
    "Under Review": "info",
    "Completed": "success",
    "Submitted": "success",
    "On Hold": "warning",
    "In Progress": "warning",
    "Canceled": "error",
    "Closed": "error",
  };
  
  const statuses = ["Under Review", "In Progress", "Completed", "On Hold", "Canceled"];
  
  return (
    <Box>
      <CustomChip
        borderRadius="4px"
        size="small"
        skin="light"
        onClick={handleClick}
        color={statusColors[tag] || "secondary"}
        sx={{
          lineHeight: 1,
          py: 1,
        }}
        label={
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <FiberManualRecordIcon fontSize="small" />
            <Typography variant="caption" sx={{ fontSize: "0.8rem" }}>
              Status:
            </Typography>
            <Typography variant="caption" sx={{ fontSize: "0.8rem", fontWeight: 660 }}>
              {tag}
            </Typography>
          </Box>
        }
      />
      
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        disableAutoFocusItem   
        disableRestoreFocus
        PaperProps={{ sx: { width: 200 } }}
      >
        {statuses.map((status) => {
          const isSelected = tag === status;

          return (
            <MenuItem
              key={status}
              selected={isSelected}
              onClick={() => handleSelect(status)}
              sx={{
                "&:hover": {
                  backgroundColor: theme.palette.action.hover,
                },
                "&.Mui-selected": {
                  backgroundColor: theme.palette.primary.main,
                  color: theme.palette.common.white,
                  "&:hover": {
                    backgroundColor: theme.palette.primary.dark,
                  },
                },
              }}
            >
              {status}
            </MenuItem>
          );
        })}
      </Menu>
    </Box>
  );
};

const WorkOrderDetail = () => {
  // ** Hooks and Vars
  const MySwal = withReactContent(Swal);
  const navigate = useNavigate();
  const theme = useTheme();

  const { requestID } = useParams();

  //#region States

  const contentHeightFixed = false;
  const [activeStep, setActiveStep] = useState(0);
  const [tabValue, setTabValue] = useState("1");
  const [loading, setLoading] = useState(false);
  const [value, setValue] = useState("1");
  const [openSearchRecordsDlg, setOpenSearchRecordsDlg] = useState(false);
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 5,
  });
  const [anchorEl, setAnchorEl] = useState(null);
  const [openScheduleAst, setOpenScheduleAst] = useState(false);
  const [schedulingReq, setSchedulingReq] = useState(false);
  const [openUploadDlg, setOpenUploadDlg] = useState(false);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(new Date());
  // const [notesSort, setNotesSort] = useState(true);
  const [openComLog, setOpenComLog] = useState(false);
  const [estTravelTime, setEstTravelTime] = useState(false);
  const [billingFreq, setBillingFreq] = useState("select");
  const [recurringType, setRecurringType] = useState("weekly");
  const [depositReq, setDepositReq] = useState(false);
  const [depositType, setDepositType] = useState("percentage");
  const [formatsBilling, setFormatsBilling] = useState([]);  
  const [monthlyOptions, setMonthlyOptions] = useState('billingDay')
  const [openInstructions, setOpenInstructions] = useState(false);
  const [isChanged, setIsChanged] = useState(false);
  const { setHasUnsavedChanges, hasUnsavedChanges } = useUnsavedChanges();
  const [openUnsavedChangesDlg, setOpenUnsavedChangesDlg] = useState(false);
  const [unsavedDialogAction, setUnsavedDialogAction] = useState({
    action: '',
    tabValue: '',
  });
  const [oldServiceRequest, setOldServiceRequest] = useState({});
  const [btnLoading, setButtonLoading] = useState(false);
  const [openJobSchedule, setOpenJobSchedule] = useState(false);
  const [openUploadDlg2, setOpenUploadDlg2] = useState(false);

  const location = useLocation();

  const handleOpenUnsavedChangesDlg = (action, tabValue) => {
    if(action === 'tabChange'){
      setUnsavedDialogAction({
        action: action,
        tabValue: tabValue,
      });
    }else{
      setUnsavedDialogAction({
        action: action,
        tabValue: '',
      });
    }
    setOpenUnsavedChangesDlg(true); 
  };
  const handleCloseUnsavedChangesDlg = () => setOpenUnsavedChangesDlg(false);
  const handleDiscardChanges = () => { 
    if(unsavedDialogAction.action === 'tabChange'){
      setOpenUnsavedChangesDlg(false); 
      setTabValue(unsavedDialogAction.tabValue)
    } else if(unsavedDialogAction.action === 'upload'){
      setOpenUnsavedChangesDlg(false); 
        // setFiles([]);
        // setUploadFiles([]);
        setOpenUploadDlg2(false); 
    } else{
      setOpenUnsavedChangesDlg(false); 
      navigate('/work-orders');
    }
  };

  const handleOpenJobSchedule = () => setOpenJobSchedule(true);
  const handleCloseJobSchedule = () => setOpenJobSchedule(false);

  const [flagList, setFlagList] = useState({
    isCustomerLoaded:false, 
    isJobsiteLoaded:false, 
    isContactsLoaded:false, 
    isPrefferedTimeLoaded:false,
    isPreferedDaysLoaded:false,
    isCompleteWorkByLoaded:false, 
    isTagsLoaded:false,
    isViewed:false
  });
  const [status, setStatus] = useState('');
  const [rules, setRules] = useState(
    { 
      canEdit: true,
      canDelete: true,
      canSubmit: true,
      nextStatuses: [],
      cannotGoBack: false
  });



  //start
  const [openNewCustomerDlg, setOpenNewCustomerDlg] = useState(false);
  const [openBulkSchedule, setOpenBulkSchedule] = useState(false);
  const [selectedToSchedule, setSelectedToSchedule] = useState([]);

  const {IndexProps} = useIndexLogic();
  const {fetchData, allCustomers} = IndexProps;

  const {CustomerProps} = useCustomerLogic({activeStep, setActiveStep,Customersteps,setOpenNewCustomerDlg, fetchData, allCustomers});

  const {setCustomer, customer, allJobsites, setAllJobsites,setIsCompleted, isCompleted, fetchContacts, contacts,
    jobsite,
    setJobsite,
    openNewJobsiteDlg,
    setOpenNewJobsiteDlg,
    handleOpenNewJobsiteDlg,
    handleCloseNewJobsiteDlg,
    handleJobsiteChange,
    handleOpenTagManagementDlg,
    //handleDeleteTag,
    handleOpenNewCustomerDlg,
    openTagManagementDlg,
    handleCloseTagManagementDlg,
    fetchJobsites
  } = CustomerProps;
  const [jobsiteVisitPayload, setJobsiteVisitPayload] = useState({
    entityID: requestID
  })

  const { JobsiteVisitProps } = useJobsiteVisitLogic(jobsiteVisitPayload, setLoading);
  const {
    columnsJobsiteVisit,
    jobsiteVisits,
    jobsiteVisitsPaginationModel,
    setJobVisitsPaginationModel
  } = JobsiteVisitProps;

  const handleClose = () => {
    setAnchorEl(null);
  };

  const { CreateWorkOrderProps } = useCreateWorkOrderLogic({handleClose,activeStep,setActiveStep,stepsRequestDetails,setLoading});

  const {
    handleFormat,
    handleWorkOrderNext,
    handleBack,
    formats,
    ddlArray,
    allContacts,
    openNewContactDlg,
    handleCloseContactDialog,
    toast,                     
    handleAddTag,
    handleDeleteTag,
    serviceRequest,
    setServiceRequest,
    Tag,
    //  handleCloseNewJobsiteDlg,
    //  openNewJobsiteDlg,
    // setOpenNewJobsiteDlg,
    propertyArea,
    areaMeasurement,
    activeStepJobsite,
    jobsiteContent,
    clearPropertyArea,
    clearAreaMeasurement,
    setActiveStepJobsite,
    setPropertyArea,
    setAreaMeasurement,
    setAlertList,
    // handleJobsiteChange,
    alertList,
    files,
    setFiles,
    NProgress,
    // fetchJobsites,
    propertyAreaList,
    setTag,
    resetRequest,
    handleCustomerDropdownChange,
    requestErrors,
    handleJobsiteDropdownChange,
    handleContactDropdownChange,
    handleCompleteWorkByDateChange,
    SaveService,
    validateRequestField,
    // handleOpenNewJobsiteDlg,
    handleOpenNewContactDlg,
    //handleTimeChange, 
    fetchPropertyArea,
    setRequestErrors, 
    handleServiceRequestChange,    
    formatCurrencyDisplay 
  } = CreateWorkOrderProps;

  const {  
    AssignToProps
  } = useAssignToLogic({serviceRequest, setLoading, requestID, requestType: "WorkOrder", navigate});

  const {
    filteredEmployees,      
    assignedUserIds,     
  } = AssignToProps;

  const availabilityDaysArray = parseAvailabilityDays(serviceRequest.availabilityDays || "[]");
  //const {ServiceProps} = useServiceLogic({setLoading,toast,navigate, handleCloseServiceDlg, serviceContent,fetchServices});
  useEffect(() => {
    // Check if we arrived from a conversion page using the flag in localStorage
    const conversionFlag = localStorage.getItem('fromConversion');

    if (conversionFlag === 'true') {
      // setIsFromConversion(true);
      setValue("6");
      
      // Optional: Clear the flag after checking (or leave it for future visits)
      localStorage.removeItem('fromConversion');
    }
  }, [location]); // Runs on location change

  ////
  // Work Requirememt section
  const [undoClicked, setUndoClicked] = useState(false);
 const [initialServiceRequest, setInitialServiceRequest] = useState(serviceRequest);
  const {
    AddServiceProps,
    AddBundleProps,
    WorkRequirementsProps,
    AttachmentProps
  } = useWorkRequirementLogic({requestID, navigate,toast, setLoading,serviceRequest});

  const {
    handleOpenBundleDlg,
    handleOpenServiceDlg,
    workRequirementList,
    fetchWorkRequirements
  } = WorkRequirementsProps;

  const { JobListProps } = useJobListLogic(requestID, 0, setLoading);
  const {
    jobList,
    columnsJobListDetail,
    jobListPaginationModel,
    setJobListPaginationModel,
    openJobDetail,
    handleCloseJobDetail,
    selectedWorkReq,
    selectedJob,
    setSelectedJob,
    fetchJobs
  } = JobListProps;
  /////end of work Requirement

  useEffect(() => {
    fetchJobs();
  }, [workRequirementList])
  
  useEffect(()=>{
    if(serviceRequest?.requestID > 0){
      setRules(getStatusRules(serviceRequest?.status));
      setStatus(serviceRequest?.status);
      //console.log(serviceRequest)
    }    
  },[serviceRequest?.requestID, serviceRequest?.status]);

  useEffect(()=>{
    if(flagList.isViewed && serviceRequest?.isViewed && serviceRequest?.status !== "In Progress"){

      const SaveStatus = async () => {
        const result = await SaveService();
      
        if (result) {
          fetchRequests(requestID, false);
          setStatus(serviceRequest.status);
          setRules(getStatusRules(serviceRequest?.status));
          toast.success("Status changed to "+serviceRequest?.status, {
            position: "top-right",
            autoClose: 3000,
            hideProgressBar: true,
            closeOnClick: true,
            pauseOnHover: true,
            draggable: true,
            progress: undefined,
              theme: "light",
              style: {
                fontFamily: '"DM Sans", sans-serif',
                fontSize: '15px',
              },
          });        
        } else {
          toast.error("Status update failed.", {
            position: "top-right",
            autoClose: 5000,
            hideProgressBar: false,
            closeOnClick: false,
            pauseOnHover: true,
            draggable: true,
            progress: undefined,
             theme: "light",
              style: {
                fontFamily: '"DM Sans", sans-serif',
                fontSize: '15px',
              },
          });
        }
      }
      
      SaveStatus();

      //fetchRequests(requestID);
      setFlagList((prev)=>({
        ...prev, "isViewed":false
      }));
    }
  },[flagList.isViewed]);

  const handleIsViewed = () => {
    if(!serviceRequest?.isViewed && (serviceRequest?.status === ('Submitted' || 'Not Viewed'))){
      setServiceRequest((prevState) => ({
        ...prevState,
        status: 'Under Review',
        isViewed : true
      }));
      setFlagList((prev)=>({
        ...prev, "isViewed":true
      }));
      setStatus('Under Review');   
    }
  };

  const handleStatusChange = (key,value) => {    
    if(value === "Completed" || value === "On Hold" || value === "Canceled"){
      
      const isFinal = status =>
        status === "Completed" ||
        status === "Canceled";

        if (
          workRequirementList.some(req =>
            req.jobs
               .filter(job => job.occurrenceNo > 0)
               .some(job => !isFinal(job.status))
          )
        ){
        toast.error(`You cannot change the status. Some jobs are pending`, {
          position: "top-right",
          autoClose: 5000,
          hideProgressBar: false,
          closeOnClick: false,
          pauseOnHover: true,
          draggable: true,
          progress: undefined,
            theme: "light",
              style: {
                fontFamily: '"DM Sans", sans-serif',
                fontSize: '15px',
              },
        });
        return;  
      }
    }
    if (canTransition(status, value)) {
      setServiceRequest((prevState) => ({
        ...prevState,
        [key]: value,
      }));
    } else {
      toast.error(`You cannot move from ${status} to ${value}.`, {
        position: "top-right",
        autoClose: 5000,
        hideProgressBar: false,
        closeOnClick: false,
        pauseOnHover: true,
        draggable: true,
        progress: undefined,
         theme: "light",
              style: {
                fontFamily: '"DM Sans", sans-serif',
                fontSize: '15px',
              },
      });
    }       
    
  }

  useEffect(() => {
    if (requestID) {
      fetchRequests(requestID, true);
    }
  }, [requestID]);

  useEffect(() => {
    if (flagList.isCustomerLoaded && flagList.isJobsiteLoaded, flagList.isContactsLoaded) {
      const timer = setTimeout(() => {
        setLoading(false);
        handleIsViewed();
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [flagList.isCustomerLoaded && flagList.isJobsiteLoaded, flagList.isContactsLoaded])

  useEffect(() => {
    fetchJobsites2(serviceRequest?.customerID);
  }, [serviceRequest?.customerID]);

  useEffect(() => {
    const isEqual = _.isEqual(oldServiceRequest, serviceRequest); // Deep compare OldEmp and employee
    setIsChanged(!isEqual);
    setHasUnsavedChanges(!isEqual);
  }, [oldServiceRequest, serviceRequest]);

  const fetchJobsites2 = async (customerID) => {
    if (customerID) {
      try {
        let jobs = await GetJobsite(customerID, 0, navigate);
        setAllJobsites(jobs);

      } catch (error) {
        console.error("Error fetching Jobsite data:", error);
      }
    }
  };
    
      useEffect(() => {
        if (serviceRequest?.customerID) {      
          fetchContacts(serviceRequest?.customerID);
          setFlagList((prev)=>({
            ...prev, "isContactsLoaded":true
          })); 
        }    
      }, [serviceRequest?.customerID]);
    
      useEffect(()=>{
        if(serviceRequest?.jobSiteID  || serviceRequest?.jobSiteID){
          // console.log('Fetching propertyarea of jobsiteID : ', serviceRequest?.jobSiteID)
          setJobsite(allJobsites.filter(x => x.jobsiteID === serviceRequest?.jobSiteID)[0]);
          fetchPropertyArea(serviceRequest?.jobSiteID);
          setFlagList((prev)=>({
            ...prev, "isJobsiteLoaded":true
          })); 
        }
      },[allJobsites]);
    
      useEffect(()=> {
        setCustomer(allCustomers.filter(x => x.customerID === serviceRequest?.customerID)[0]); 
        setIsCompleted(true);  
        //fetchWorkRequirements(requestID);     
        setFlagList((prev)=>({
          ...prev, "isCustomerLoaded":true
        }));     
      },[allCustomers,serviceRequest?.customerID]);


      const fetchRequests = async (RequestID = requestID, loading = true) => { 
        try {
          setLoading(true);      
          let RequestRaw = await GetServiceRequestRaw(0, RequestID);
          const newRequest = RequestRaw.result[0];
          setServiceRequest(newRequest);
          setOldServiceRequest(newRequest);
           if (!undoClicked) {
          setInitialServiceRequest(newRequest);
        }
        } catch (error) {
          console.error("Error fetching Requests data:", error);
        } finally {
          setLoading(false);
        }
      };
    
      const validateBasicFields = () => {
        const tempErrors = {};
  
        if (!serviceRequest.requestTitle) 
          tempErrors.requestTitle = 'Request Title is required';

        if (serviceRequest.isDeposit && !serviceRequest.depositAmount)
          tempErrors.depositAmount = 'Deposit Amount must be greater than 0';
  
        if (!serviceRequest.customerID) 
            tempErrors.customerID = 'Customer is required';
    
        if (!serviceRequest.jobSiteID || !allJobsites?.some((jobsite) => jobsite.jobsiteID === serviceRequest?.jobSiteID)) 
            tempErrors.jobSiteID = 'Jobsite is required';
    
        if (!serviceRequest.jobsiteContactID || !contacts?.some((contact) => contact.id === serviceRequest?.jobsiteContactID)) 
            tempErrors.jobsiteContactID = 'Jobsite Contact is required';
    
    
        setRequestErrors(tempErrors);
    
        return Object.keys(tempErrors).length === 0;
    };
     
     const handleUndo = async () => {
    const newValue = oldServiceRequest;
    setServiceRequest(newValue);

    const result = await SaveService(newValue);
    if(!result.isError){
      fetchRequests();
    }
  };

    const handleSaveServiceRequest = async () => {
      if (!validateBasicFields()) {
        return;
      }    
      setButtonLoading(true);    
      try {
        const result = await SaveService();
        
        if (result) {
          SendNotification(navigate);
          fetchRequests(requestID, true);
          setStatus(serviceRequest.status);
          setRules(getStatusRules(serviceRequest?.status));
           if (!undoClicked) {
            toast.success(
              ({}) => (
                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                >
                  <Typography sx={{ flex: 1 }}>
                    Work Order updated successfully.
                  </Typography>
                  <Button
                    variant="contained"
                    color="main"
                    size="small"
                    sx={{
                      mr: 3,
                      borderRadius: "20px", 
                      minWidth: "64px", 
                    }}
                    onClick={(e) => {
                      e.preventDefault();
                      handleUndo();
                    }}
                  >
                    Undo
                  </Button>
                </Box>
              ),
          {
            position: "top-right",
            autoClose: 5000,
            hideProgressBar: false,
            closeOnClick: false,
            pauseOnHover: true,
            draggable: true,
            progress: undefined,
             theme: "light",
              style: {
                fontFamily: '"DM Sans", sans-serif',
                fontSize: '15px',
              },
          });  
        }      
        } else {
          toast.error("Work Order update failed.", {
            position: "top-right",
            autoClose: 5000,
            hideProgressBar: false,
            closeOnClick: false,
            pauseOnHover: true,
            draggable: true,
            progress: undefined,
             theme: "light",
              style: {
                fontFamily: '"DM Sans", sans-serif',
                fontSize: '15px',
              },
          });
        }
        
      } catch (e) {
        console.error(e);
      } finally {
        setButtonLoading(false);          
      }
    };
  
    const handleTimeChange = (timeValue, isSelected) => {
      try {
        const timeKey = Number(Object.keys(timeMapping).find((key) => timeMapping[key] === timeValue));
        let updatedTimes = JSON.parse(serviceRequest.availabilityTime || "[]");
    
        if (isSelected) {
          if (timeKey === 1) {
            // If "Anytime" is selected, replace all times with [1, 2, 3, 4]
            updatedTimes = [1, 2, 3, 4];
          } else {
            // Add the specific time
            updatedTimes = [...new Set([...updatedTimes, timeKey])];
            // Remove "Anytime" if another time is selected
            updatedTimes = updatedTimes.filter((t) => t !== 1);
    
            // Automatically check "Anytime" if all 2, 3, 4 are selected
            if (updatedTimes.includes(2) && updatedTimes.includes(3) && updatedTimes.includes(4)) {
              updatedTimes = [1, 2, 3, 4];
            }
          }
        } else {
          // Remove the time from the list
          updatedTimes = updatedTimes.filter((t) => t !== timeKey);
    
          // If "Anytime" is being deselected, clear all times
          if (timeKey === 1) {
            updatedTimes = [];
          } else {
            // Uncheck "Anytime" if any specific time is deselected
            updatedTimes = updatedTimes.filter((t) => t !== 1);
          }
        }
    
        // Update the serviceRequest state
        setServiceRequest((prevState) => ({
          ...prevState,
          availabilityTime: JSON.stringify(updatedTimes),
        }));
          
      } catch (e) {
        console.log("Error in handleTimeChange:", e);
      }
    };
    const handleDayChange = (day) => {
      // Parse the availabilityDays string into an actual array of numbers
      let updatedDays = serviceRequest.availabilityDays
        ? parseAvailabilityDays(serviceRequest.availabilityDays)
        : [];
  
      // Get the numeric value of the selected day (e.g., "Mon" -> 1)
      const dayNumber = dayToNumberMap[day];
  
      // Toggle the day in the list
      if (updatedDays.includes(dayNumber)) {
        // If the day is already selected, remove it
        updatedDays = updatedDays.filter((d) => d !== dayNumber);
      } else {
        // If the day is not selected, add it
        updatedDays.push(dayNumber);
      }
  
      // Update the serviceRequest state, storing only numbers
      setServiceRequest((prevState) => ({
        ...prevState,
        availabilityDays: JSON.stringify(updatedDays), // Store as a clean stringified array of numbers
      }));
    };
  
    const dayToNumberMap = {
      Mon: 1,
      Tue: 2,
      Wed: 3,
      Thu: 4,
      Fri: 5,
      Sat: 6,
      Sun: 0,
    };
  
    const daysMapping = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
    const timeMapping = {
      1: "Anytime",
      2: "Morning",
      3: "Afternoon",
      4: "Evening",
    };
  
    const allDays = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
    const allTimes = ["Anytime", "Morning", "Afternoon", "Evening"];
  
    useEffect(() => {
      if (Object.keys(requestErrors).length > 0) {
        const timer = setTimeout(() => {
          setRequestErrors([]);
        }, 5000);
        return () => clearTimeout(timer);
      }
      }, [requestErrors]);

  //End of new code

  const handleEstTravelTimeChange = () => setEstTravelTime(!estTravelTime);

  const handleBillingFreqChange = (value) => setBillingFreq(value);
  const handleRecurringTypeChange = (value) => setRecurringType(value);

  const handledDepositReqChange = () => setDepositReq(!depositReq);
  const handleDepositTypeChange = (value) => setDepositType(value);

  const handleFormatBilling = (event, newFormats) => {
    setFormatsBilling(newFormats);
  };

  const handleOpenScheduleAstDlg = () => setOpenScheduleAst(true);
  const handleCloseScheduleAstDlg = () => setOpenScheduleAst(false);


  const handleOpenUploadDlg = () => {
    setOpenUploadDlg(true);
  };

  const handleOpenNewComLogDlg = () => setOpenComLog(true);
  const handleCloseNewComLogDlg = () => setOpenComLog(false);

  //#endregion states

  const handleTabChange = (event, newValue) => {
    setValue(newValue);
  };

  const handleOpenInstructionsDlg = () => setOpenInstructions(true);
  const handleCloseInstructionsDlg = () => setOpenInstructions(false);

  const handleOpenSearchRecordsDlg = () => setOpenSearchRecordsDlg(true);
  const handleCloseSearchRecordsDlg = () => setOpenSearchRecordsDlg(false);

  const handleNavigation = (to) => {   
    if(isChanged){
       handleOpenUnsavedChangesDlg('navigate');

      
      // MySwal.fire({
      //   title: "Are you sure?",
      //   html: "This page has unsaved changes",
        
      //   showCancelButton: true,
      //   confirmButtonColor: "#23A26D",
      //   cancelButtonColor: "#d33",
      //   confirmButtonText: "Discard Changes",
      //   cancelButtonText: "Cancel",
      //   allowEscapeKey: true,
      // }).then((result) => {
      //   if (result.isConfirmed) {
      //     navigate(-1);
      //     setHasUnsavedChanges(false);
      //   }
      // });
    }else{
      navigate(to);
    }
  };

  useEffect(() => {
      const handleBeforeUnload = (e) => {
        if (hasUnsavedChanges) {
          e.preventDefault();
          e.returnValue = '';
        }
      };
    
       const handleBackButton = (event) => {
      if (hasUnsavedChanges) {
        event.preventDefault();

         handleOpenUnsavedChangesDlg('navigate');

          // MySwal.fire({
          //   title: "Are you sure?",
          //   html: "This page has unsaved changes",
          //   showCancelButton: true,
          //   confirmButtonColor: "#23A26D",
          //   cancelButtonColor: "#d33",
          //   confirmButtonText:"Discard Changes",
          //   cancelButtonText: "Cancel",
          //   allowEscapeKey: true,
          // }).then((result) => {
          //   if (result.isConfirmed) {
          //     navigate(-1);
          //     setHasUnsavedChanges(false);
          //   }
          // });
        }else{
          navigate('/work-orders');
        }
      };
    
      window.history.pushState(null, '', window.location.href);
    
      window.addEventListener('beforeunload', handleBeforeUnload);
      window.addEventListener('popstate', handleBackButton);
    
      return () => {
        window.removeEventListener('beforeunload', handleBeforeUnload);
        window.removeEventListener('popstate', handleBackButton);
      };
    }, [hasUnsavedChanges]);

  const handlePageBack = () => {
    handleNavigation("/work-orders");
  };

  const getInitials = (name) => {
    if (!name) return "";
    const nameParts = name.split(" ");
    return nameParts
      .map((part) => part[0]?.toUpperCase())
      .join("")
      .slice(0, 2);
  };

  const handleSendWODepositInvoice = async () => {
    try{
      setLoading(true);
      
      const requestID = serviceRequest?.requestID;
      const customerID = serviceRequest?.customerID;
      const depositAmount = serviceRequest?.depositAmount;
      const filteredWR = workRequirementList.find((wr) => wr.subServiceID === 0);
      const jobID = filteredWR?.jobs?.[0].jobID;
      
      if(depositAmount <= 0){
        toast.error('Deposit amount must be greater than 0', {
      style: {
        fontFamily: '"DM Sans", sans-serif',
        fontSize: '15px',
      },
    });
        return;
      }

      const response = await SendWODepositInvoice(requestID, customerID, depositAmount, jobID, navigate);

      if(!response.isError){
        toast.success(response.message)
        fetchRequests();
      }else{
        toast.error(response.message || "Failed to send invoice", {
      style: {
        fontFamily: '"DM Sans", sans-serif',
        fontSize: '15px',
      },
    });
      }
    }catch(error){
      console.error(error)
    }finally{
      setLoading(false);
    }
  }

  const allServicesScheduled =
  Array.isArray(workRequirementList) &&
  workRequirementList.length > 0 &&
  workRequirementList.every(svc =>
    (svc.jobs ?? []).some(job => job.occurrenceNo > 0)
  );

  const formatCurrency = (value) => {
    if (value === '' || value === null || value === undefined) return '';
    
    const num = typeof value === 'string' ? parseFloat(value.replace(/,/g, '')) : value;
    
    if (isNaN(num)) return '';
    
    const hasDecimals = num % 1 !== 0;
    
    return num.toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
    });
  };

  const depositStyles = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between'
  }

  return (
    <UserLayout
      contentHeightFixed={contentHeightFixed}
      activeLink={"Work Orders"}
      showIcon={true}
      NavURL={handlePageBack}
    >
      <Card sx={{ width: "100%", height: "100%", position: "relative" }}>
        <CardHeader
          title={
             <Box
                 sx={{
                   display: "flex",
                   flexDirection: { xs: "column", md: "row" },
                   justifyContent: "space-between",
                   alignItems: { xs: "flex-start", md: "center" },
                   gap: { xs: 2, md: 0 }
                 }}
               >
                 {/* Left Side - Stacked in mobile */}
                 <Box sx={{ 
                   display: "flex", 
                   flexDirection: { xs: "column", md: "row" },
                   alignItems: { xs: "flex-start", md: "center" },
                   gap: { xs: 1, md: 2 },
                   width: { xs: "100%", md: "auto" }
                 }}>
                <Typography variant="h3" sx={{ fontWeight: "bold" }}>
                  WRO-{serviceRequest?.requestID}
                </Typography>
                <HtmlTooltip
                  title={
                    <Box sx={{ p: (4, 2) }}>
                      <Box>
                        <Typography
                          color="text.primary"
                          fontSize="small"
                          fontWeight="600"
                          mb={2}
                        >
                          Customer Details
                        </Typography>
                        <Box sx={{ display: "flex", gap: 20 }}>
                          <Box
                            sx={{
                              display: "flex",
                              flexDirection: "column",
                              gap: 2,
                            }}
                          >
                            {customer?.customerType === 'Business' ? (
                              <Typography color="text.secondary" fontSize="small">
                                Company Name
                              </Typography>
                            ) : (
                              <Typography color="text.secondary" fontSize="small">
                                Customer Name
                              </Typography>
                            )}
                            <Typography color="text.secondary" fontSize="small">
                              Email
                            </Typography>
                            <Typography color="text.secondary" fontSize="small">
                              Phone
                            </Typography>
                          </Box>
                          <Box
                            sx={{
                              display: "flex",
                              flexDirection: "column",
                              gap: 2,
                            }}
                          >
                            {customer?.customerType === 'Business' ? (
                              <Typography fontSize="small">
                                {customer?.companyName}
                              </Typography>
                            ) : (
                              <Typography fontSize="small">
                                {serviceRequest?.customerName}
                              </Typography>
                            )}
                            <Typography fontSize="small">
                              {customer?.email}
                            </Typography>
                            <Typography fontSize="small">
                              {customer?.phoneNumbers[0].phoneNo}
                            </Typography>
                          </Box>
                        </Box>
                      </Box>
                    </Box>
                  }
                >
                  <Box>
                    <CustomChip
                      sx={{ cursor: "pointer" }}
                      label={
                        <Typography variant="body1" color="primary" sx={{ maxWidth: '200px', overflow: 'hidden' }}>
                          {serviceRequest?.requestTitle}
                        </Typography>
                      }
                      color="secondary"
                      skin={theme.palette.mode === "dark" ? 'dark' : 'light'}
                      icon={<Icon icon="tabler:building" />}
                    />
                  </Box>
                </HtmlTooltip>
                {/* <CustomChip label="Internal" color="info" skin="light" /> */}
               <CustomChipWithDropdown
                tag={serviceRequest?.status}
                onSelect={(selectedTag) => {
                  handleStatusChange('status', selectedTag);
                }}
              />
                <AvatarGroup
                  className="pull-up"
                  max={4}
                  sx={{
                    "& .MuiAvatar-root": { width: 25, height: 25 },
                    "& .MuiAvatarGroup-avatar": { width: 25, height: 25 },
                  }}
                >
                  {filteredEmployees && filteredEmployees
                    .filter(employee => assignedUserIds.includes(employee.employeeID))
                    .map((employee, index) => {
                      const employeeFullName = `${employee.firstName} ${employee.lastName}`;
                      return (
                        <Tooltip key={employee.employeeID} title={employeeFullName}>
                          <Avatar
                            src={employee.avatarUrl || `/images/avatars/${index + 1}.png`}
                            alt={employeeFullName}
                            sx={{ width: 25, height: 25 }}
                          />
                        </Tooltip>
                      );
                    })}
                </AvatarGroup>
              </Box>
              <Box sx={{ display: "flex", gap: 2 }}>
                  <Button variant="contained" color="main" onClick={() => handleSaveServiceRequest()} disabled={btnLoading || !isChanged} >
                    {btnLoading ? <CircularProgress size={24} /> : 'Save Changes'}
                  </Button>
                  
                <Button 
                  variant='outlined' 
                  color={theme.palette.mode === 'dark' ? "secondary" : "primary"}
                  startIcon={<Icon icon='tabler:calendar' />}
                  disabled={
                    !rules.canEdit 
                    || (workRequirementList && workRequirementList.length === 0)
                    || allServicesScheduled
                  }
                  onClick={() => setOpenBulkSchedule(true)}
                >
                  Bulk Schedule
                </Button>
                {/* <PopoverButton
                  handleOpenSchedule={handleOpenScheduleAstDlg}
                  schedulingReq={schedulingReq}
                  setSchedulingReq={setSchedulingReq}
                /> */}
                {serviceRequest?.isDeposit && (
                  <>
                    <Button
                      variant='contained'
                      color='main'
                      disabled={isChanged}
                      onClick={handleSendWODepositInvoice}
                    >
                      {serviceRequest?.depositInvoiceSent ? 'Resend Invoice' : 'Send Invoice'}
                    </Button>
                  </>
                )}
              </Box>
            </Box>
          }
        />
        <CardContent>
          <Box>
            <TabContext value={value}>
              <TabList
                sx={{ mb: 2 }}
                onChange={handleTabChange}
                scrollButtons="auto"
                variant="scrollable"
                aria-label="simple tabs example"
              >
                <Tab
                  value="1"
                  label={
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1.5,
                        width: "100%",
                        color: theme.palette.mode === 'dark' ? 
                          value === "1" ? "#fff" : "inherit" :
                          "inherit"
                      }}
                    >
                      <Box
                        sx={{
                          fontSize: { xs: '0.875rem', md: "1rem" },
                          fontWeight: 500,
                          whiteSpace: "nowrap",
                        }}
                      >
                        Work Order Details
                      </Box>
                    </Box>
                  }
                />
                <Tab
                  value="2"
                  label={
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1.5,
                        width: "100%",
                        color: theme.palette.mode === 'dark' ? 
                          value === "2" ? "#fff" : "inherit" :
                          "inherit"
                      }}
                    >
                      <Box
                        sx={{
                          fontSize: { xs: '0.875rem', md: "1rem" },
                          fontWeight: 500,
                          whiteSpace: "nowrap",
                        }}
                      >
                        Work Requirements
                      </Box>
                    </Box>
                  }
                />
                <Tab
                  value="7"
                  label={
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1.5,
                        width: "100%",
                        color: theme.palette.mode === 'dark' ? 
                          value === "7" ? "#fff" : "inherit" :
                          "inherit"
                      }}
                    >
                      <Box
                        sx={{
                          fontSize: { xs: '0.875rem', md: "1rem" },
                          fontWeight: 500,
                          whiteSpace: "nowrap",
                        }}
                      >
                        All Jobs
                      </Box>
                    </Box>
                  }
                />
                <Tab
                  value="3"
                  label={
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1.5,
                        width: "100%",
                        color: theme.palette.mode === 'dark' ? 
                          value === "3" ? "#fff" : "inherit" :
                          "inherit"
                      }}
                    >
                      <Box
                        sx={{
                          fontSize: { xs: '0.875rem', md: "1rem" },
                          fontWeight: 500,
                          whiteSpace: "nowrap",
                        }}
                      >
                        Attachments
                      </Box>
                    </Box>
                  }
                />
                <Tab
                  value="4"
                  label={
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1.5,
                        width: "100%",
                        color: theme.palette.mode === 'dark' ? 
                          value === "4" ? "#fff" : "inherit" :
                          "inherit"
                      }}
                    >
                      <Box
                        sx={{
                          fontSize: { xs: '0.875rem', md: "1rem" },
                          fontWeight: 500,
                          whiteSpace: "nowrap",
                        }}
                      >
                        Notes
                      </Box>
                    </Box>
                  }
                />
                <Tab
                  value="5"
                  label={
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1.5,
                        width: "100%",
                        color: theme.palette.mode === 'dark' ? 
                          value === "5" ? "#fff" : "inherit" :
                          "inherit"
                      }}
                    >
                      <Box
                        sx={{
                          fontSize: { xs: '0.875rem', md: "1rem" },
                          fontWeight: 500,
                          whiteSpace: "nowrap",
                        }}
                      >
                        Linked Records
                      </Box>
                    </Box>
                  }
                />
                <Tab
                  value="6"
                  label={
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1.5,
                        width: "100%",
                        color: theme.palette.mode === 'dark' ? 
                          value === "6" ? "#fff" : "inherit" :
                          "inherit"
                      }}
                    >
                      <Box
                        sx={{
                          fontSize: { xs: '0.875rem', md: "1rem" },
                          fontWeight: 500,
                          whiteSpace: "nowrap",
                        }}
                      >
                        Assignees
                      </Box>
                    </Box>
                  }
                />
              </TabList>
              <TabPanel value="1" sx={{ px: { xs: 0, md: 4 } }}>
                <Box
                  sx={{
                    display: 'flex',
                    gap: { md: 8, xs: 0 },
                    flexDirection: { md: 'row', xs: 'column' }
                    }}
                  >
                    {/* Left Column */}
                    <Box sx={{ 
                      width: { md: '30%', xs: '100%' }, 
                      maxWidth: { md: '30%', xs: '100%' } 
                    }}>
                    <Grid 
                      container 
                      spacing={5}
                    >
                      <Grid item xs={12}>
                        <Divider
                          sx={{
                            color: "text.disabled",
                            "& .MuiDivider-wrapper": { px: 6 },
                            fontSize: theme.typography.body2.fontSize,
                            my: (theme) => `${theme.spacing(3)} !important`,
                          }}
                        >
                          Basic Information
                        </Divider>
                      </Grid>
                      <Grid item xs={12}>
                        <CustomTextField
                          fullWidth
                          label="Work Order Title"
                          disabled={!rules.canEdit}
                          required
                          placeholder="Enter a brief Work order title"
                          error={!!requestErrors.requestTitle}
                          helperText={requestErrors.requestTitle}
                          onChange={(e) => {
                            setServiceRequest((prevState) => ({
                              ...prevState,
                              requestTitle: e.target.value,
                            }));
                          }}
                          value={serviceRequest?.requestTitle}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <CustomTextField
                          fullWidth
                          label="Description"
                          multiline
                          rows={2}
                          disabled={!rules.canEdit}
                          placeholder="Add a description..."
                          onChange={(e) => {
                            setServiceRequest((prevState) => ({
                              ...prevState,
                              reqDescription: e.target.value,
                            }));
                          }}
                          value={serviceRequest?.reqDescription}
                        />
                      </Grid>
                      <Grid item xs={12}>
                        <FormLabel sx={{ fontSize: "13px", color: "text.primary", mb: '4px' }}>
                          Customer Name <span style={{ color: theme.palette.error.main }}>*</span>
                        </FormLabel>
                        <Box sx={{
                          border: `1px solid rgba(47, 43, 61, 0.2)`,
                          p: 2,
                          borderRadius: 1,
                          cursor: 'default',
                          backgroundColor: 'action.disabledBackground'
                        }}>
                          <Typography variant="body1">
                            {customer?.customerType === 'Individual' ? 
                            `${customer?.firstName} ${customer?.lastName}` :
                            customer?.customerType === 'Business' ?
                            customer?.companyName :
                            'N/A'}
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={12}>
                        <CustomAutocomplete
                          fullWidth
                          required
                          disabled={!customer?.customerID}
                          options={[
                            { jobsiteID: "create-new", jobsiteName: "+ Create New Jobsite", isCreateOption: true },
                            ...((allJobsites || [])
                              .filter(option => option.jobsiteName)
                              .sort((a, b) => a.jobsiteName.localeCompare(b.jobsiteName)))
                          ]}
                          getOptionLabel={(option) => option.jobsiteName || ""}
                          value={
                            allJobsites?.find(
                              (jobsite) => jobsite.jobsiteID === serviceRequest?.jobSiteID
                            ) || null
                          }
                          onChange={(event, newValue) => {
                            if (newValue?.isCreateOption) {
                              if (customer?.customerID) {
                                handleOpenNewJobsiteDlg();
                              }
                            } else if (newValue) {
                              handleJobsiteDropdownChange(newValue.jobsiteID);
                            }
                          }}
                          isOptionEqualToValue={(option, value) =>
                            option.jobsiteID === value.jobsiteID
                          }
                          filterOptions={(options, params) => {
                            const filtered = options
                              .filter((option) => {
                                if (option.isCreateOption) return true;
                                const jobsiteName = option.jobsiteName || "";
                                return jobsiteName.toLowerCase().includes(params.inputValue.toLowerCase());
                              })
                              .filter(option => option.isCreateOption || option.jobsiteName);
                            return filtered;
                          }}
                          renderOption={(props, option) => (
                            <li {...props}>
                              <span style={{ 
                                fontWeight: option.isCreateOption ? 'bold' : 'normal',
                                color: option.isCreateOption && !customer?.customerID ? 'rgba(0, 0, 0, 0.38)' : 'inherit',
                              }}>
                                {option.jobsiteName}
                              </span>
                            </li>
                          )}
                          renderInput={(params) => (
                            <CustomTextField
                              {...params}
                              label="Jobsite"
                              required
                              placeholder="Select Jobsite"
                              error={!!requestErrors.jobSiteID}
                              helperText={requestErrors.jobSiteID}
                            />
                          )}
                        />
                      </Grid>
                        <Grid item xs={12}>
                        <CustomAutocomplete
                          fullWidth
                          required
                          disabled={!rules.canEdit}
                          id="jobsite-contact-autocomplete"
                          options={[
                            { id: "create-new", contact: "+ Create New Contact", isCreateOption: true },
                            ...((contacts || []).sort((a, b) => (a.contact || "").localeCompare(b.contact || "")))
                          ]}
                          getOptionLabel={(option) => option.contact || ""}
                          value={
                            contacts?.find(
                              (contact) => contact.id === serviceRequest?.jobsiteContactID
                            ) || null
                          }
                          onChange={(event, newValue) => {
                            if (newValue?.isCreateOption) {
                              handleOpenNewContactDlg();
                            } else if (newValue) {
                              handleContactDropdownChange(newValue.id);
                            }
                          }}
                          isOptionEqualToValue={(option, value) =>
                            option.id === value.id
                          }
                          filterOptions={(options, params) => {
                            return options.filter((option) => {
                              if (option.isCreateOption) return true;
                              return (option.contact || "").toLowerCase().includes(params.inputValue.toLowerCase());
                            });
                          }}
                          renderOption={(props, option) => (
                            <li {...props}>
                              <span style={{ 
                                fontWeight: option.isCreateOption ? 'bold' : 'normal',
                              }}>
                                {option.contact}
                              </span>
                            </li>
                          )}
                          renderInput={(params) => (
                            <CustomTextField
                              {...params}
                              label="Jobsite Contact"
                              required
                              placeholder="Select Jobsite Contact"
                              error={!!requestErrors.jobsiteContactID}
                              helperText={requestErrors.jobsiteContactID}
                            />
                          )}
                        />
                      </Grid>
                      <Grid item xs={12} sm={12}>
                        <Divider
                          sx={{
                            color: "text.disabled",
                            "& .MuiDivider-wrapper": { px: 6 },
                            fontSize: theme.typography.body2.fontSize,
                            my: (theme) => `${theme.spacing(3)} !important`,
                          }}
                        >
                          Availability Details
                        </Divider>
                      </Grid>
                      <Grid item xs={12}>
                        <Box sx={{ display: "flex", flexDirection: "column" }}>
                          <FormLabel sx={{ fontSize: "13px", color: "text.primary" }}>
                            Preferred Times
                          </FormLabel>
                          <FormControl component="fieldset">
                            {allTimes.map((time, index) => {

                              // Check if "Anytime" is checked
                              const isAnytimeChecked = serviceRequest?.availabilityTime.includes('1');

                              // Determine if the current time should be checked
                              const isChecked = isAnytimeChecked ||
                                serviceRequest?.availabilityTime.includes(
                                  Number(Object.keys(timeMapping).find((key) => timeMapping[key] === time))
                                );
                              return (
                                <FormControlLabel
                                  key={index}
                                  control={
                                    <Checkbox
                                    disabled={!rules.canEdit}
                                      checked={
                                        isChecked
                                      }
                                      onChange={(e) => handleTimeChange(time, e.target.checked)}
                                      color="success"
                                    />
                                  }
                                  label={time}
                                />
                              )
                            })}
                          </FormControl>
                        </Box>
                      </Grid>
                      <Grid item xs={12}>
                        <Box sx={{ display: "flex", flexDirection: "column" }}>
                          <FormLabel
                            sx={{
                              fontSize: "13px",
                              color: "text.primary",
                              marginBottom: "4px",
                            }}
                          >
                            Preferred Days
                          </FormLabel>
                          <ToggleButtonGroup value={availabilityDaysArray}>
                            {allDays.map((day, index) => {
                              const dayNumber = dayToNumberMap[day];
                              return (
                                <ToggleButton
                                  // disabled={!rules?.canEdit || dayNumber === 0 || dayNumber === 6}
                                  key={index}
                                  value={day}
                                  color={'info'}
                                  selected={availabilityDaysArray.includes(dayNumber)} // Mark selected ones
                                  onChange={() => handleDayChange(day)}
                                   sx={{
                              fontSize: { xs: "11px", sm: "13px" },
                              px: { xs: 1, sm: 2 },
                              minWidth: { xs: "40px", sm: "60px" },
                            }}
                                >
                                  {day}
                                </ToggleButton>
                              );
                            })}
                          </ToggleButtonGroup>
                        </Box>
                      </Grid>
                      <Grid item xs={12} md={8}>
                        <FormLabel
                          sx={{
                            fontWeight: 400,
                            fontSize: "13px",
                            marginBottom: "4px",
                            color: "text.primary",
                            height: "100%",
                          }}
                        >
                          Complete Work By
                        </FormLabel>
                        {loading ? (
                          <p></p> // Show a loading indicator while data is being fetched
                        ) : (
                          <LocalizationProvider dateAdapter={AdapterDayjs}>
                            <DatePicker
                              sx={{ width: '100%' }}
                              disabled={!rules.canEdit}
                              value={serviceRequest?.completeWorkBy && serviceRequest?.completeWorkBy !== "0001-01-01T00:00:00" && serviceRequest?.completeWorkBy !== "1900-01-01T00:00:00" ? dayjs(serviceRequest.completeWorkBy) : null}
                              onChange={(newValue) => {
                                handleCompleteWorkByDateChange(newValue);
                              }}
                              slotProps={{ textField: { size: 'small' } }}
                            />
                          </LocalizationProvider>
                        )}
                      </Grid>
                    </Grid>
                  </Box>
                  <Divider orientation='vertical' flexItem />
                  <Box sx={{ 
                       width: { md: '70%', xs: '100%' }, 
                       maxWidth: { md: '70%', xs: '100%' },
                       mt: { xs: 0, md: 0 }
                     }}>
                    <Grid 
                      container 
                      spacing={5}
                    >
                      <Grid item xs={12} sm={12}>
                        <Divider
                          sx={{
                            color: "text.disabled",
                            "& .MuiDivider-wrapper": { px: 6 },
                            fontSize: theme.typography.body2.fontSize,
                            my: (theme) => `${theme.spacing(3)} !important`,
                          }}
                        >
                          Deposit Details
                        </Divider>
                      </Grid>
                      <Grid item xs={12} md={10} lg={6.1}>
                        <Box
                          sx={{
                            border: "1px solid #DBDBDB",
                            borderRadius: 1,
                            p: 2,
                          }}
                        >
                          <FormControlLabel
                            control={
                              <Checkbox
                                color="success"
                                checked={serviceRequest?.isDeposit || false}
                                disabled={!rules.canEdit || serviceRequest?.depositInvoiceSent}
                                onChange={(e) => handleServiceRequestChange('isDeposit', e.target.checked)}
                              />
                            }
                            label={
                              <>
                                <Typography variant="body1" color="text.primary">
                                  Deposit Required
                                </Typography>
                                <Typography variant="caption">
                                  Check this if a deposit is required
                                </Typography>
                              </>
                            }
                          />
                        </Box>
                      </Grid>
                      {serviceRequest?.isDeposit && (
                        <>
                          <Grid item xs={12} md={10} lg={6}>
                            <CustomTextField
                              fullWidth
                              required
                              label='Deposit Amount'
                              placeholder='Enter deposit amount'
                              disabled={!rules.canEdit || serviceRequest?.depositInvoiceSent}
                              value={formatCurrencyDisplay(serviceRequest?.depositAmount)}
                              onChange={(e) => {
                                const numericValue = e.target.value.replace(/[^0-9.]/g, '');
                                handleServiceRequestChange('depositAmount', numericValue);
                              }}
                              InputProps={{
                                startAdornment: (<InputAdornment position='start'>$</InputAdornment>),
                              }}
                              error={!!requestErrors.depositAmount}
                              helperText={requestErrors.depositAmount}
                            />
                          </Grid>
                          <Grid item xs={12} md={8}>
                            <Box
                              sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: 2,
                                border: "1px solid #DBDBDB",
                                borderRadius: 1,
                                p: 4,
                              }}
                            >
                              <Typography variant='h5'>Work Deposit</Typography>
                              <Box sx={depositStyles}>
                                <Typography variant='body1'>Due</Typography>
                                <Typography variant='body1' fontWeight='bold'>${
                                    serviceRequest?.depositAmountPaid >= serviceRequest?.depositAmount ?
                                    formatCurrency(0) :
                                    formatCurrency((serviceRequest?.depositAmount - serviceRequest?.depositAmountPaid) || 0)
                                  }
                                </Typography>
                              </Box>
                              <Box sx={depositStyles}>
                                <Typography variant='body1'>Paid</Typography>
                                <Typography variant='body1' fontWeight='bold'>${formatCurrency(serviceRequest?.depositAmountPaid || 0)}</Typography>
                              </Box>
                              <Box sx={depositStyles}>
                                <Typography variant='body1'>Applied</Typography>
                                <Typography variant='body1' fontWeight='bold'>${formatCurrency(serviceRequest?.depositUtilizedAmount || 0)}</Typography>
                              </Box>
                              <Box>
                                <Divider
                                  sx={{
                                    color: "text.disabled",
                                    "& .MuiDivider-wrapper": { px: 6 },
                                    fontSize: theme.typography.body2.fontSize,
                                    my: (theme) => `${theme.spacing(3)} !important`,
                                  }}
                                />
                              </Box>
                              <Box sx={{...depositStyles, color: theme.palette.main.main}}>
                                <Typography variant='body1' fontWeight='bold' color='main.main'>Remaining</Typography>
                                <Typography variant='body1' fontWeight='bold' color='main.main'>${
                                    formatCurrency(((serviceRequest?.depositAmountPaid || 0) - (serviceRequest?.depositUtilizedAmount || 0)) || 0)
                                  }
                                </Typography>
                              </Box>
                            </Box>
                          </Grid>
                        </>
                      )}
                      <Grid item xs={12} sm={12}>
                        <Divider
                          sx={{
                            color: "text.disabled",
                            "& .MuiDivider-wrapper": { px: 6 },
                            fontSize: theme.typography.body2.fontSize,
                            my: (theme) => `${theme.spacing(6)} !important`,
                          }}
                        >
                          Additional Details
                        </Divider>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: 'center', gap: 1 }}>
                          <Typography variant="body1" sx={{ mr: 6,}}>
                            Tags
                          </Typography>
                          <IconButton onClick={() => { handleOpenTagManagementDlg() }}  
                          // disabled={!rules.canEdit}
                          >
                            <AddIcon />
                          </IconButton>
                          {serviceRequest?.tags && serviceRequest.tags.length > 0 ? serviceRequest.tags.map((tag) => (
                            <CustomChip
                              label={tag.tagName}
                              skin='light'
                              color='info'
                              // disabled={!rules.canEdit}
                              deleteIcon={<Tooltip title='Click to remove tag'><CloseIcon /></Tooltip>}
                              onDelete={() => handleDeleteTag(tag.tagName)}
                              sx={{
                                cursor: 'pointer',
                                mr: 2,
                              }}
                            />
                          ))
                            :
                            (
                              <Typography variant="body2" sx={{ ml: 2, mt: 2, }}>
                                No tags
                              </Typography>
                            )
                          }
                        </Box>
                      </Grid>
                      <Grid item xs={12} sm={6}>
                        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                          <Button sx={{ position: 'absolute', bottom: 20, right: 20 }} variant="contained" color="main" onClick={() => handleSaveServiceRequest()} disabled={btnLoading || !isChanged} >
                            {btnLoading ? <CircularProgress size={24} /> : 'Save Changes'}
                          </Button>
                        </Box>
                      </Grid>
                    </Grid>
                  </Box>
                </Box>
              </TabPanel>
              <TabPanel value="2" sx={{ px: { xs: 0, md: 4 } }}>
                <Grid container spacing={5}>
                  {(workRequirementList && workRequirementList.length === 0) ? (
                      <>
                        <Grid item xs={12} md={6}>
                          <Card>
                            <CardContent
                              sx={{
                                display: "flex",
                                textAlign: "center",
                                alignItems: "center",
                                flexDirection: "column",
                                backgroundColor: theme.palette.mode === 'dark' ? '#1B2431' : "#FBFBFC",
                                p: (theme) =>
                                  `${theme.spacing(9.75, 5, 9.25)} !important`,
                              }}
                            >
                              <CustomAvatar skin="light" color="info" sx={{ mb: 2.25 }}>
                                <Icon icon='tabler:building' />
                              </CustomAvatar>
                              <Typography variant="h5" sx={{ mb: 2.75 }}>
                                No Bundles added yet
                              </Typography>
                              <Typography sx={{ mb: 6, color: "text.secondary" }}>
                              Add bundles that are required by clicking on
                              “Add Bundle” button
                              </Typography>
                              <Button
                                variant="outlined"
                                color={theme.palette.mode === 'dark' ? "secondary" : "primary"}
                                startIcon={<AddIcon color="info" />}
                                onClick={handleOpenBundleDlg}
                                disabled={!rules.canEdit}
                              >
                                Add Bundle
                              </Button>
                            </CardContent>
                          </Card>
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <Card>
                            <CardContent
                              sx={{
                                display: "flex",
                                textAlign: "center",
                                alignItems: "center",
                                flexDirection: "column",
                                backgroundColor: theme.palette.mode === 'dark' ? '#1B2431' : "#FBFBFC",
                                p: (theme) =>
                                  `${theme.spacing(9.75, 5, 9.25)} !important`,
                              }}
                            >
                              <CustomAvatar skin="light" color="info" sx={{ mb: 2.25 }}>
                                <Icon icon='tabler:building' />
                              </CustomAvatar>
                              <Typography variant="h5" sx={{ mb: 2.75 }}>
                                No Services added yet
                              </Typography>
                              <Typography sx={{ mb: 6, color: "text.secondary" }}>
                              Add services that are required by clicking on
                              “Add Service” button
                              </Typography>
                              <Button
                                variant="outlined"
                                color={theme.palette.mode === 'dark' ? "secondary" : "primary"}
                                startIcon={<AddIcon color="info" />}
                                onClick={handleOpenServiceDlg}
                                disabled={!rules.canEdit}
                              >
                                Add Service
                              </Button>
                            </CardContent>
                          </Card>
                        </Grid>
                      </>
                    ) : (  
                    <WorkRequirements
                      entity="workOrder-detail"           
                      method="detail"         
                      {...WorkRequirementsProps} 
                      rules={rules} 
                      requestID={requestID}   
                      fetchRequests={fetchRequests} 
                    />
                    )}
                </Grid>
              </TabPanel>  
              <TabPanel value="7" sx={{ px: { xs: 0, md: 4 } }}>
                <Grid container spacing={5}>
                  <Grid item xs={12}>
                    <Card>
                      <CardHeader
                        title='Job List'
                      />
                      <CardContent>
                        <DataGridPro
                          // pagination
                          rows={jobList}
                          rowHeight={62}
                          columns={columnsJobListDetail}
                          // pageSizeOptions={[10, 50, { value: jobList.length, label: "Show All" }]}
                          disableRowSelectionOnClick
                          // paginationModel={jobListPaginationModel}
                          // onPaginationModelChange={setJobListPaginationModel}
                          slots={{
                            footer: () => null
                          }}
                          sx={{
                            minHeight: 350,
                            maxHeight: 500,
                            "& .MuiDataGrid-columnHeaderTitle": {
                              textTransform: "none",
                            },
                          }}
                        />
                      </CardContent>
                    </Card>
                  </Grid>
                  <Grid item xs={12}>
                    <Card>
                      <CardHeader
                        title='Jobsite Visits'
                      />
                      <CardContent>
                        <DataGridPro
                          // pagination
                          rows={jobsiteVisits}
                          rowHeight={62}
                          columns={columnsJobsiteVisit}
                          // pageSizeOptions={[10, 50, { value: jobsiteVisits.length, label: "Show All" }]}
                          disableRowSelectionOnClick
                          // paginationModel={jobsiteVisitsPaginationModel}
                          // onPaginationModelChange={setJobVisitsPaginationModel}
                          slots={{
                            footer: () => null
                          }}
                          sx={{
                            minHeight: 350,
                            maxHeight: 500,
                            "& .MuiDataGrid-columnHeaderTitle": {
                              textTransform: "none",
                            },
                          }}
                        />
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>
              </TabPanel>            
              <TabPanel value="3" sx={{ px: { xs: 0, md: 4 } }}>
                <AttachmentsComponent 
                  rules={rules}
                  request={serviceRequest}
                  requestType={"WorkOrder"}
                  handleOpenUnsavedChangesDlg={handleOpenUnsavedChangesDlg}
                  theme={theme}
                  loading={loading}
                  AttachmentProps={AttachmentProps}
                  openUploadDlg2={openUploadDlg2}
                  setOpenUploadDlg2={setOpenUploadDlg2}
                />                
              </TabPanel>
              <TabPanel value="4" sx={{ px: { xs: 0, md: 4 } }}>
                <Box sx={{
                  height: '70vh',
                }}>
                  <NotesComponent 
                    requestID={requestID}
                    requestType={"workorder"}
                    customerID={serviceRequest?.customerID}
                    setLoading={setLoading}
                    rules={rules}                    
                  />
                </Box>
              </TabPanel>
              <TabPanel value="5" sx={{ px: { xs: 0, md: 4 } }}>
                <LinkedRecordsComponent 
                  requestID={requestID} 
                  customerID={serviceRequest?.customerID}
                  setLoading={setLoading}
                  module="detail"
                  rules={rules}
                />
              </TabPanel>
              <TabPanel value="6" sx={{ px: { xs: 0, md: 4 } }}>
                <Grid container spacing={5}>
                <AssignToComponent 
                    requestID={requestID}
                    serviceRequest={serviceRequest}
                    requestType={"WorkOrder"}
                    setLoading={setLoading}
                    rules={rules}    
                    navigate= {navigate} 
                    handleServiceRequestChange={handleServiceRequestChange}                                   
                  />
                </Grid>
              </TabPanel>
            </TabContext>
          </Box>
        </CardContent>
      </Card>

      {/* Popup Dialogs */}
      <SpecialInstructionsDialog 
        open={openInstructions}
        handleClose={handleCloseInstructionsDlg}
      />
     <UnsavedChangesDialog open={openUnsavedChangesDlg} handleClose={handleCloseUnsavedChangesDlg} handleDiscard={handleDiscardChanges} />
      <AddContactDialog
        open={openNewContactDlg}
        handleClose={handleCloseContactDialog}
        customer={customer}
        setLoading={setLoading}
        fetchContactData={fetchContacts}
        toast={toast}
        contacts={allContacts}
      />

        <AddServiceDialog          
          {...AddServiceProps}                 
        />        

        <AddBundleDialog          
          {...AddBundleProps}
        />

      <PreAttachment        
        Title="Upload Attachment"
        {...AttachmentProps}
      />

      {/* <CreateServiceDialog
        open={openServiceDlg}
        handleClose={handleCloseServiceDlg}
        serviceContent={serviceContent}
        ServiceProps={ServiceProps}
      /> */}

      <TagManagementDialog
        open={openTagManagementDlg}
        handleClose={handleCloseTagManagementDlg}
        data={{ title: 'Tag Management' }}
        handleAddTag={handleAddTag}
        handleDeleteTag={handleDeleteTag}
        tags={serviceRequest?.tags}
        tag={Tag}
        setTag={setTag}
      />

      {/* <JobScheduleDialog 
        open={openJobSchedule}
        handleClose={handleCloseJobSchedule}
      /> */}
      <BulkScheduleDialog
        open={openBulkSchedule}
        options={workRequirementList}
        selected={selectedToSchedule}
        onChange={setSelectedToSchedule}
        onClose={() => setOpenBulkSchedule(false)}
        onNext={() => {
          setOpenBulkSchedule(false);
          setOpenJobSchedule(true);
        }}
        
      />

      {/* reuse your existing scheduler, now pointing at detail-fetch + requestID */}
      <JobScheduleDialog
        open={openJobSchedule}
        handleClose={handleCloseJobSchedule}
        services={selectedToSchedule}
        fetchWorkRequirements={fetchWorkRequirements}
        requestID={requestID}
        toast={toast}      
        onBack={()=>{
          setOpenJobSchedule(false);
          setOpenBulkSchedule(true);
        }}  
      /> 
      
      <AddJobsiteDialog
                   open={openNewJobsiteDlg}
                   handleClose={handleCloseNewJobsiteDlg}
                   setJobsiteOpen={setOpenNewJobsiteDlg}
                   customer={customer}
                   fetchContactData={fetchContacts}
                   toast={toast}
                   jobsite={jobsite}
                   handleJobsiteChange={handleJobsiteChange}
                   navigate={navigate}
                   NProgress={NProgress}
                   fetchContacts={fetchContacts}
                   fetchJobsites={fetchJobsites}
                   contacts={allContacts}
                   propertyArea={propertyArea}
                   setPropertyArea={setPropertyArea}
                   areaMeasurement={areaMeasurement}
                   setAreaMeasurement={setAreaMeasurement}
                   setLoading={setLoading}
                   activeStep={activeStepJobsite}
                   setActiveStep={setActiveStepJobsite}
                   clearAreaMeasurement={clearAreaMeasurement}
                   fetchPropertyArea={fetchPropertyArea}
                   propertyAreaList={propertyAreaList}
                   clearPropertyArea={clearPropertyArea}
                   jobsiteContent={jobsiteContent}
                   alertList={alertList}
                   setAlertList={setAlertList}
                   files={files}
                   setFiles={setFiles}
                 />
      <JobDetailDialog
        open={openJobDetail}
        handleClose={handleCloseJobDetail}
        method={'detail'}
        entity={'workOrder-detail'} 
        rules={rules} 
        workReq={selectedWorkReq} 
        job={selectedJob}
        setJob={setSelectedJob}
        fetchWorkRequirements={fetchWorkRequirements}
        setLoading={setLoading} 
        toast={toast}
        fetchRequests={fetchRequests}       
      />

      <Backdrop
        sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 10000 }}
        open={loading}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
    </UserLayout>
  );
};

export default WorkOrderDetail;
